import { db } from './firebase';
import { auth } from './firebaseAuth';
import { 
  collection, 
  doc, 
  setDoc, 
  onSnapshot, 
  deleteDoc, 
  getDocs,
  query,
  where,
  Query
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { Company, User, OrdenProduccion, DisposicionProducto, ActivityLog } from '../types';
import { INITIAL_USERS, INITIAL_ORDENES, INITIAL_DISPOSICIONES } from '../data/mockData';
import { DEFAULT_COMPANY } from './saasUtils';

type SyncCallback<T> = (data: T[]) => void;

class SyncRegistry {
  private listeners: { [key: string]: SyncCallback<any>[] } = {};

  subscribe<T>(key: string, callback: SyncCallback<T>): () => void {
    if (!this.listeners[key]) {
      this.listeners[key] = [];
    }
    this.listeners[key].push(callback);
    return () => {
      this.listeners[key] = this.listeners[key].filter(cb => cb !== callback);
    };
  }

  notify<T>(key: string, data: T[]) {
    if (this.listeners[key]) {
      this.listeners[key].forEach(cb => {
        try {
          cb(data);
        } catch (err) {
          console.error(`Error executing subscriber callback for key: ${key}`, err);
        }
      });
    }
  }
}

export const syncRegistry = new SyncRegistry();

// Fallback safety flags
let isFirebaseConnected = true;

// Active listeners references
const activeUnsubscribers: { [key: string]: () => void } = {};

function mapToUniqueCollection(collectionName: string): string {
  if (collectionName === 'user_auth') return 'insumedic_user_auth';
  if (collectionName === 'companies') return 'insumedic_companies_v3';
  if (collectionName === 'users') return 'insumedic_users_v3';
  if (collectionName === 'ordenes') return 'insumedic_ordenes_v3';
  if (collectionName === 'disposiciones') return 'insumedic_disposiciones_v3';
  if (collectionName === 'backups') return 'insumedic_backups_v3';
  if (collectionName === 'logs') return 'insumedic_logs_v3';
  return collectionName;
}

// ==========================================
// MANDATORY SYSTEM DIAGNOSTIC ERROR HANDLERS
// ==========================================

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  let authUser: any = null;
  try {
    authUser = getAuth().currentUser;
  } catch (e) {
    // Auth SDK might not be loaded yet
  }

  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: authUser?.uid || null,
      email: authUser?.email || null,
      emailVerified: authUser?.emailVerified || null,
      isAnonymous: authUser?.isAnonymous || null,
      tenantId: authUser?.tenantId || null,
      providerInfo: authUser?.providerData?.map((provider: any) => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };

  const formattedError = `Firestore Error: ${JSON.stringify(errInfo)}`;
  console.error(formattedError);
  throw new Error(JSON.stringify(errInfo));
}

function checkAndHandleFirestoreError(error: any, operationType: OperationType, path: string | null) {
  const errMsg = String(error?.message || error).toLowerCase();
  const isPermissionErr = errMsg.includes('permission') || errMsg.includes('permissão') || error?.code === 'permission-denied';
  if (isPermissionErr) {
    handleFirestoreError(error, operationType, path);
  }
}

/**
 * Clear all active realtime synchronization listeners
 */
export function cleanupAllRealtimeSync(): void {
  Object.keys(activeUnsubscribers).forEach((key) => {
    try {
      if (typeof activeUnsubscribers[key] === 'function') {
        activeUnsubscribers[key]();
      }
    } catch (e) {
      console.warn(`Error cleaning up listener ${key}:`, e);
    }
    delete activeUnsubscribers[key];
  });
}

/**
 * Upload single item to firestore (fail-safe)
 */
export async function uploadItemToFirestore<T extends { id: string }>(
  collectionName: string, 
  item: T
): Promise<void> {
  if (!isFirebaseConnected) return;
  // Guard: only execute cloud writes if user is authenticated in Firebase Auth
  const currentAuth = auth.currentUser;
  if (!currentAuth) {
    return;
  }

  const dbCollection = mapToUniqueCollection(collectionName);
  try {
    let payload: any = { ...item };
    // Security sanitization: strip plain password if writing to user collection
    if (collectionName === 'users' && payload.password) {
      delete payload.password;
    }
    const docRef = doc(db, dbCollection, item.id);
    await setDoc(docRef, payload);
  } catch (error: any) {
    try {
      checkAndHandleFirestoreError(error, OperationType.WRITE, `${dbCollection}/${item.id}`);
    } catch (_) {}
    console.warn(`Firestore upload failed for ${dbCollection}/${item.id}, working in local-only mode:`, error);
  }
}

/**
 * Delete single item from firestore (fail-safe)
 */
export async function deleteItemFromFirestore(
  collectionName: string, 
  id: string
): Promise<void> {
  if (!isFirebaseConnected) return;
  // Guard: only execute cloud deletes if user is authenticated in Firebase Auth
  const currentAuth = auth.currentUser;
  if (!currentAuth) {
    return;
  }

  const dbCollection = mapToUniqueCollection(collectionName);
  try {
    const docRef = doc(db, dbCollection, id);
    await deleteDoc(docRef);
  } catch (error: any) {
    try {
      checkAndHandleFirestoreError(error, OperationType.DELETE, `${dbCollection}/${id}`);
    } catch (_) {}
    console.warn(`Firestore delete failed for ${dbCollection}/${id}:`, error);
  }
}

/**
 * Setup Realtime synchronization with Firestore for a collection
 * Supports optional tenantCompanyId isolation.
 */
export function setupRealtimeSync<T extends { id: string }>(
  collectionName: string,
  localStorageKey: string,
  initialData: T[],
  tenantCompanyId?: string
): () => void {
  const listenerKey = tenantCompanyId ? `${collectionName}_${tenantCompanyId}` : collectionName;

  // If we already have an active listener for this collection/tenant, return it
  if (activeUnsubscribers[listenerKey]) {
    return activeUnsubscribers[listenerKey];
  }

  // Guard: Do not initiate Firestore listener if not authenticated in Firebase Auth
  const currentAuth = auth.currentUser;
  if (!currentAuth) {
    // Load existing local data into sync registry for offline display without firing network errors
    const savedLocal = localStorage.getItem(localStorageKey);
    if (savedLocal) {
      try {
        const parsed = JSON.parse(savedLocal);
        if (Array.isArray(parsed)) {
          syncRegistry.notify(collectionName, parsed);
        }
      } catch (e) {
        syncRegistry.notify(collectionName, initialData);
      }
    } else {
      syncRegistry.notify(collectionName, initialData);
    }
    return () => {};
  }

  const dbCollection = mapToUniqueCollection(collectionName);

  try {
    const colRef = collection(db, dbCollection);
    const targetQuery: Query = tenantCompanyId 
      ? (collectionName === 'companies' ? query(colRef, where('id', '==', tenantCompanyId)) : query(colRef, where('companyId', '==', tenantCompanyId)))
      : colRef;

    const unsub = onSnapshot(
      targetQuery,
      (snapshot) => {
        if (snapshot.empty) {
          // If Firestore is empty for this query/collection:
          // Check local storage for offline resilience, and safely upload local items to cloud if present
          const savedLocal = localStorage.getItem(localStorageKey);
          let dataToUse: T[] = [];
          
          if (savedLocal) {
            try {
              const parsed = JSON.parse(savedLocal);
              if (Array.isArray(parsed) && parsed.length > 0) {
                dataToUse = tenantCompanyId 
                  ? parsed.filter((item: any) => !item.companyId || item.companyId === tenantCompanyId)
                  : parsed;
              }
            } catch (e) {
              dataToUse = [];
            }
          }
          
          if (dataToUse.length === 0) {
            if (collectionName === 'ordenes') {
              dataToUse = (tenantCompanyId ? INITIAL_ORDENES.filter(o => o.companyId === tenantCompanyId) : INITIAL_ORDENES) as unknown as T[];
            } else if (collectionName === 'disposiciones') {
              dataToUse = (tenantCompanyId ? INITIAL_DISPOSICIONES.filter(d => d.companyId === tenantCompanyId) : INITIAL_DISPOSICIONES) as unknown as T[];
            } else if (collectionName === 'users') {
              dataToUse = (tenantCompanyId ? INITIAL_USERS.filter(u => u.companyId === tenantCompanyId) : INITIAL_USERS) as unknown as T[];
            } else if (collectionName === 'companies') {
              dataToUse = [DEFAULT_COMPANY] as unknown as T[];
            }
          }

          // Sync items to cloud so other browsers/incognito and team members can access them
          dataToUse.forEach((item) => {
            uploadItemToFirestore(collectionName, item).catch(() => {});
          });
          
          // Notify React state
          localStorage.setItem(localStorageKey, JSON.stringify(dataToUse));
          syncRegistry.notify(collectionName, dataToUse);
        } else {
          const list: T[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data() as T;
            if (data) {
              list.push({
                ...data,
                id: data.id || doc.id
              });
            }
          });

          // Check if local storage has any additional items created locally that aren't in Firestore yet
          const savedLocal = localStorage.getItem(localStorageKey);
          if (savedLocal) {
            try {
              const parsed = JSON.parse(savedLocal);
              if (Array.isArray(parsed)) {
                const cloudIds = new Set(list.map(i => i.id));
                parsed.forEach((localItem: any) => {
                  if (localItem && localItem.id && !cloudIds.has(localItem.id)) {
                    // Upload missing local item to cloud
                    uploadItemToFirestore(collectionName, localItem).catch(() => {});
                    list.push(localItem);
                  }
                });
              }
            } catch (e) {}
          }

          // Guarantee baseline initial records (e.g. Tramadol rejected disposition id: '2') are always present and uploaded to cloud
          if (collectionName === 'disposiciones') {
            INITIAL_DISPOSICIONES.forEach((initDisp) => {
              if ((!tenantCompanyId || initDisp.companyId === tenantCompanyId) && !list.some(d => (d as any).id === initDisp.id)) {
                list.unshift(initDisp as unknown as T);
                uploadItemToFirestore(collectionName, initDisp).catch(() => {});
              }
            });
          } else if (collectionName === 'ordenes') {
            INITIAL_ORDENES.forEach((initOrd) => {
              if ((!tenantCompanyId || initOrd.companyId === tenantCompanyId) && !list.some(o => (o as any).id === initOrd.id)) {
                list.unshift(initOrd as unknown as T);
                uploadItemToFirestore(collectionName, initOrd).catch(() => {});
              }
            });
          }

          // Update localStorage for resilience, and notify React subscribers
          localStorage.setItem(localStorageKey, JSON.stringify(list));
          syncRegistry.notify(collectionName, list);
        }
      },
      (error) => {
        try {
          checkAndHandleFirestoreError(error, OperationType.LIST, dbCollection);
        } catch (_) {
          // Diagnostic error was logged inside handleFirestoreError. Continue to fallback.
        }
        console.warn(`Firestore real-time sync notice for ${dbCollection}. Fallback active.`, error);
        
        // Safe fall back to local storage loading
        const saved = localStorage.getItem(localStorageKey);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            syncRegistry.notify(collectionName, Array.isArray(parsed) ? parsed : initialData);
          } catch (e) {
            syncRegistry.notify(collectionName, initialData);
          }
        } else {
          syncRegistry.notify(collectionName, initialData);
        }
      }
    );

    activeUnsubscribers[listenerKey] = unsub;
    return unsub;
  } catch (e: any) {
    try {
      checkAndHandleFirestoreError(e, OperationType.LIST, dbCollection);
    } catch (_) {
      // Diagnostic error was logged inside handleFirestoreError.
    }
    console.error(`Failed to register collection listener for ${dbCollection}`, e);
    return () => {};
  }
}

/**
 * Force a one-time direct HTTP getDocs sync from Firestore, bypassing any socket or snapshot blockages.
 * Performs bidirectional reconciliation to ensure no locally saved items are lost and all browsers get fresh cloud data.
 */
export async function forceSyncFromFirestore(tenantCompanyId?: string): Promise<{ success: boolean; message: string }> {
  // Guard: Do not execute getDocs on protected collections if not authenticated
  const currentAuth = auth.currentUser;
  if (!currentAuth) {
    return { success: false, message: 'Usuario no autenticado en Firebase. Se mantiene modo local/offline.' };
  }

  try {
    const collectionsToSync = [
      { key: 'companies', localKey: 'insumedic_companies' },
      { key: 'users', localKey: 'insumedic_users' },
      { key: 'ordenes', localKey: 'insumedic_ordenes_v3' },
      { key: 'disposiciones', localKey: 'insumedic_disposiciones_v3' },
      { key: 'backups', localKey: 'insumedic_backups_v3' },
      { key: 'logs', localKey: 'insumedic_activity_logs' },
    ];

    for (const colInfo of collectionsToSync) {
      const dbCollection = mapToUniqueCollection(colInfo.key);
      const colRef = collection(db, dbCollection);
      
      const targetQuery: Query = (tenantCompanyId && colInfo.key !== 'companies')
        ? query(colRef, where('companyId', '==', tenantCompanyId))
        : (tenantCompanyId && colInfo.key === 'companies')
        ? query(colRef, where('id', '==', tenantCompanyId))
        : colRef;

      let querySnapshot;
      try {
        querySnapshot = await getDocs(targetQuery);
      } catch (error: any) {
        try {
          checkAndHandleFirestoreError(error, OperationType.GET, dbCollection);
        } catch (_) {}
        console.warn(`forceSync: skipping ${dbCollection} due to error:`, error?.message || error);
        continue;
      }
      
      const cloudList: any[] = [];
      if (!querySnapshot.empty) {
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data) {
            cloudList.push({
              ...data,
              id: data.id || docSnap.id
            });
          }
        });
      }

      // Check local storage
      let localList: any[] = [];
      const savedLocal = localStorage.getItem(colInfo.localKey);
      if (savedLocal) {
        try {
          const parsed = JSON.parse(savedLocal);
          if (Array.isArray(parsed)) {
            localList = parsed;
          }
        } catch (e) {}
      }

      // Reconcile: If local has items not in cloud, push them to Firestore
      const cloudIds = new Set(cloudList.map(i => i.id));
      const mergedList = [...cloudList];

      for (const localItem of localList) {
        if (localItem && localItem.id && !cloudIds.has(localItem.id)) {
          // If filtering by tenant, only push matching tenant items
          if (!tenantCompanyId || !localItem.companyId || localItem.companyId === tenantCompanyId || localItem.id === tenantCompanyId) {
            try {
              await uploadItemToFirestore(colInfo.key, localItem);
              mergedList.push(localItem);
            } catch (err) {
              console.warn(`Reconciliation upload failed for ${colInfo.key}/${localItem.id}:`, err);
            }
          }
        }
      }

      // Reconcile baseline initial items if missing from both cloud and local
      if (colInfo.key === 'disposiciones') {
        for (const initDisp of INITIAL_DISPOSICIONES) {
          if ((!tenantCompanyId || initDisp.companyId === tenantCompanyId) && !mergedList.some(d => d.id === initDisp.id)) {
            try {
              await uploadItemToFirestore('disposiciones', initDisp);
              mergedList.unshift(initDisp);
            } catch (_) {}
          }
        }
      } else if (colInfo.key === 'ordenes') {
        for (const initOrd of INITIAL_ORDENES) {
          if ((!tenantCompanyId || initOrd.companyId === tenantCompanyId) && !mergedList.some(o => o.id === initOrd.id)) {
            try {
              await uploadItemToFirestore('ordenes', initOrd);
              mergedList.unshift(initOrd);
            } catch (_) {}
          }
        }
      }

      // Update localStorage with reconciled list and notify subscribers
      localStorage.setItem(colInfo.localKey, JSON.stringify(mergedList));
      syncRegistry.notify(colInfo.key, mergedList);
    }
    return { success: true, message: 'Sincronización con la base de datos central en la nube completada exitosamente.' };
  } catch (error: any) {
    console.error('Error in forceSyncFromFirestore:', error);
    return { success: false, message: `Error de conexión con la nube: ${error?.message || error}` };
  }
}
